import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

// The showcase reads the SAME manifest the docs read. Add an applet to
// public/docs-content/features/_index.json (regenerated by
// scripts/check-features.mjs --write from the macOS AppFeature registry) and it
// appears here, in /docs, and at /features/<slug> with no code change.
export const FEATURES_MANIFEST_URL = '/docs-content/features/_index.json';

export function useFeaturesManifest() {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(FEATURES_MANIFEST_URL, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`Feature list unavailable (${r.status})`);
        return r.json();
      })
      .then((m) => { if (!cancelled) setManifest(m); })
      .catch((e) => { if (!cancelled) setError(e.message || String(e)); });
    return () => { cancelled = true; };
  }, []);

  return { manifest, error };
}

// Screenshot, or — when a feature has none — a tile in the applet's own accent
// colour with its monogram. Never renders a broken <img>: a load failure swaps
// to the same tile.
export function FeatureThumb({ feature, height = 168, rounded = 0 }) {
  const [failed, setFailed] = useState(false);
  const monogram = feature.title
    .replace(/^AI\s+/, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  if (feature.image && !failed) {
    return (
      <div style={{
        height, overflow: 'hidden', background: '#0f172a',
        borderRadius: rounded, position: 'relative', flexShrink: 0,
      }}>
        <img
          src={feature.image}
          alt={`${feature.title} running in mChatAI+`}
          loading="lazy"
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left', display: 'block' }}
        />
      </div>
    );
  }

  return (
    <div
      aria-hidden="true"
      style={{
        height,
        flexShrink: 0,
        borderRadius: rounded,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(140deg, ${feature.accent}26 0%, ${feature.accent}0d 60%, #0f172a08 100%)`,
        borderBottom: `1px solid ${feature.accent}33`,
      }}
    >
      <div style={{
        width: 62, height: 62, borderRadius: 16,
        background: feature.accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.02em',
        boxShadow: `0 6px 18px ${feature.accent}55`,
      }}>
        {monogram}
      </div>
    </div>
  );
}

function Badge({ children, tone = 'muted' }) {
  const tones = {
    muted: { bg: '#f1f5f9', fg: '#475569', bd: '#e2e8f0' },
    lab: { bg: '#faf5ff', fg: '#7e22ce', bd: '#e9d5ff' },
    core: { bg: '#eff6ff', fg: '#1d4ed8', bd: '#dbeafe' },
  };
  const t = tones[tone] || tones.muted;
  return (
    <span style={{
      display: 'inline-block', fontSize: '0.66rem', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.05em',
      padding: '3px 7px', borderRadius: 4,
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
    }}>{children}</span>
  );
}

function FeatureCard({ feature }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={`/features/${feature.slug}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', flexDirection: 'column', textDecoration: 'none',
        background: '#fff',
        border: `1px solid ${hover ? feature.accent : '#e5e7eb'}`,
        borderRadius: 12, overflow: 'hidden',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? '0 10px 26px rgba(15,23,42,0.10)' : '0 1px 2px rgba(15,23,42,0.04)',
        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
        height: '100%',
      }}
    >
      <FeatureThumb feature={feature} height={168} />
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 7, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>{feature.title}</span>
          {feature.experimental && <Badge tone="lab">Experimental</Badge>}
        </div>
        <p style={{ margin: 0, fontSize: '0.86rem', color: '#4b5563', lineHeight: 1.55, flex: 1 }}>
          {feature.hook}
        </p>
        <span style={{
          fontSize: '0.8rem', fontWeight: 600,
          color: hover ? feature.accent : '#6b7280', transition: 'color 0.15s',
        }}>
          See how it works →
        </span>
      </div>
    </Link>
  );
}

export default function FeaturesPage() {
  const { manifest, error } = useFeaturesManifest();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const grouped = useMemo(() => {
    if (!manifest) return [];
    return (manifest.categories || [])
      .map((c) => ({ ...c, items: (manifest.features || []).filter((f) => f.category === c.id) }))
      .filter((c) => c.items.length > 0);
  }, [manifest]);

  const total = manifest?.features?.length || 0;
  const onHome = manifest ? manifest.features.filter((f) => !f.experimental).length : 0;

  return (
    <div className="mp-page" style={{ padding: 0, background: '#fff' }}>
      {/* Hero */}
      <section style={{ padding: '72px 24px 8px', maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
        <span className="trust-badge trust-official" style={{ marginBottom: 16, display: 'inline-block' }}>
          macOS 15.5+
        </span>
        <h1 style={{ fontSize: '2.7rem', fontWeight: 800, color: '#111827', margin: '0 0 16px', lineHeight: 1.1 }}>
          Everything mChatAI+ does
        </h1>
        <p style={{ fontSize: '1.12rem', color: '#4b5563', lineHeight: 1.65, margin: '0 auto', maxWidth: 720 }}>
          One native Mac app, {total} applets. Chat, writing, audio, reading, maps, decks, data,
          video — plus a build surface that turns a sentence into a working mini-app.
          Every screenshot below is the real app.
        </p>
        {manifest && (
          <p style={{ fontSize: '0.92rem', color: '#6b7280', marginTop: 14 }}>
            {onHome} are on the home screen out of the box; {total - onHome} more are in the direct
            download, behind its Show Experimental switch.
          </p>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 28 }}>
          <Link to="/macos-app" className="mp-cta-button" style={{ textDecoration: 'none' }}>
            Get mChatAI+ for macOS
          </Link>
          <Link
            to="/docs"
            style={{
              background: '#fff', color: '#111827', padding: '10px 24px', borderRadius: 8,
              textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem', border: '1px solid #d1d5db',
            }}
          >
            Read the docs
          </Link>
        </div>
      </section>

      {/* Hero screenshot */}
      <section style={{ maxWidth: 1180, margin: '44px auto 8px', padding: '0 24px' }}>
        <div style={{
          borderRadius: 14, overflow: 'hidden', border: '1px solid #e5e7eb',
          boxShadow: '0 20px 50px rgba(15,23,42,0.13)', background: '#0f172a',
        }}>
          <img
            src="/assets/features/_home.jpg"
            alt="The mChatAI+ home screen on macOS, showing applets grouped into Core, Audio, Knowledge and Tools bands"
            style={{ width: '100%', display: 'block' }}
          />
        </div>
        <p style={{ textAlign: 'center', fontSize: '0.82rem', color: '#6b7280', marginTop: 12 }}>
          The home screen. You choose which applets sit in Core and which stay tucked away.
        </p>
      </section>

      {error && (
        <div style={{
          maxWidth: 720, margin: '32px auto', padding: 16, borderRadius: 8,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
        }}>
          Could not load the feature list: {error}
        </div>
      )}

      {!manifest && !error && (
        <p style={{ textAlign: 'center', color: '#6b7280', padding: '64px 24px' }}>Loading features…</p>
      )}

      {/* Category sections */}
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '48px 24px 24px' }}>
        {grouped.map((cat) => (
          <section key={cat.id} id={cat.id} style={{ marginBottom: 64, scrollMarginTop: 80 }}>
            <div style={{ marginBottom: 20, borderTop: '1px solid #e5e7eb', paddingTop: 28 }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>
                {cat.title}
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#9ca3af', marginLeft: 10 }}>
                  {cat.items.length}
                </span>
              </h2>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '0.92rem', lineHeight: 1.6, maxWidth: 720 }}>
                {cat.blurb}
              </p>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(285px, 1fr))',
              gap: 20,
            }}>
              {cat.items.map((f) => <FeatureCard key={f.slug} feature={f} />)}
            </div>
          </section>
        ))}
      </div>

      {/* CTA */}
      <section style={{ background: '#f9fafb', padding: '60px 24px', borderTop: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.7rem', fontWeight: 800, color: '#111827', margin: '0 0 12px' }}>
            Try it on your own Mac
          </h2>
          <p style={{ fontSize: '1rem', color: '#4b5563', margin: '0 0 26px', lineHeight: 1.7 }}>
            Bring your own API keys, or run entirely on-device with Apple's Foundation model and
            Ollama. Your documents, recordings and automations stay on your machine.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/macos-app" className="mp-cta-button" style={{ textDecoration: 'none' }}>
              Download mChatAI+
            </Link>
            <Link
              to="/hub"
              style={{
                background: '#fff', color: '#111827', padding: '10px 24px', borderRadius: 8,
                textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem', border: '1px solid #d1d5db',
              }}
            >
              Browse the Hub
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
